import { useState } from "react";
import { useLocale, useTranslations } from "@/lib/i18n/intl";
import { pickLocalized } from "@/lib/i18n/localized";
import { keys } from "@/lib/i18n/portal";
import type { SubscriptionPlanView } from "@/lib/api/types";
import { ApiJsonClientError } from "@/lib/query/core/api-json-client";
import { Banner, Button, Modal, useToast } from "@/shared/rd";
import { useDeleteSubscriptionPlanMutation } from "../services/useDeleteSubscriptionPlanMutation";

/**
 * Confirms a plan delete — it can't be undone, so it is a modal, then a
 * success toast. Stores already on the plan keep it.
 */
export function SubscriptionPlanDeleteModal({
  plan,
  onClose,
}: {
  plan: SubscriptionPlanView | null;
  onClose: () => void;
}) {
  const t = useTranslations();
  const toast = useToast();
  const locale = useLocale();
  const { mutateAsync: deletePlan, isPending } = useDeleteSubscriptionPlanMutation();
  const [error, setError] = useState<string | null>(null);

  const close = () => {
    setError(null);
    onClose();
  };

  const handleDelete = async () => {
    if (!plan) return;
    setError(null);
    try {
      await deletePlan({ subscriptionId: plan.id });
    } catch (caught) {
      setError(caught instanceof ApiJsonClientError ? caught.message || "" : "");
      return;
    }
    toast.show({ message: t("admin.subscriptionPlanDeletedToast", { name: pickLocalized(locale, plan.nameAr, plan.nameEn) }) });
    close();
  };

  return (
    <Modal
      open={plan !== null}
      onClose={isPending ? undefined : close}
      title={t(keys.admin.subscriptionPlanDeleteConfirmTitle)}
      width={440}
      actions={
        <>
          <Button variant="ghost" muted disabled={isPending} onClick={close}>
            {t(keys.common.cancel)}
          </Button>
          <Button
            variant="danger"
            loading={isPending}
            loadingLabel={t("admin.subscriptionPlanDeleting")}
            onClick={() => void handleDelete()}
          >
            {error !== null ? t(keys.shell.retry) : t(keys.common.delete)}
          </Button>
        </>
      }
    >
      <p className="rd-modal-text" style={{ margin: 0 }}>
        {t(keys.admin.subscriptionPlanDeleteConfirmBody)}
      </p>
      {error !== null ? (
        <Banner tone="error" title={t("admin.subscriptionPlanDeleteFailedTitle")}>
          {[error, t("admin.subscriptionPlanNothingChanged")].filter(Boolean).join(" ")}
        </Banner>
      ) : null}
    </Modal>
  );
}
